// src/services/api/apiClient.js
import { apiRequest, apiConfig } from "../../config/authConfig";
import { InteractionRequiredAuthError } from "@azure/msal-browser";

// We'll get the msalInstance from the React context instead of creating our own
let msalInstance = null;

// Function to set the MSAL instance (called from components)
export function setMsalInstance(instance) {
  msalInstance = instance;
  console.log("MSAL instance set in apiClient");
}

export async function acquireToken() {
  if (!msalInstance) {
    throw new Error(
      "MSAL instance not initialized. Make sure you are using this within a MsalProvider."
    );
  }

  const accounts = msalInstance.getAllAccounts();
  if (accounts.length === 0) throw new Error("No accounts found");

  try {
    console.log("Attempting to acquire token silently...");
    const response = await msalInstance.acquireTokenSilent({
      ...apiRequest, // Use apiRequest instead of loginRequest for backend API access
      account: accounts[0],
    });
    console.log("Token acquired successfully");
    return response.accessToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) {
      console.log("Silent token acquisition failed, trying popup...");
      const response = await msalInstance.acquireTokenPopup(apiRequest); // Use apiRequest here too
      console.log("Token acquired via popup");
      return response.accessToken;
    } else {
      console.error("Error acquiring token:", e);
      throw e;
    }
  }
}

export async function makeApiRequest(endpoint, options = {}) {
  try {
    console.log("Making API request to:", endpoint);
    const token = await acquireToken();
    console.log("Token acquired, making request...");

    let headers = {
      ...(options.headers || {}),
      Authorization: `Bearer ${token}`,
    };

    let body = options.body;
    // If body is FormData, don't set Content-Type and don't stringify
    if (body instanceof FormData) {
      // Let browser set Content-Type
    } else if (body && typeof body === "object") {
      headers["Content-Type"] = "application/json";
      body = JSON.stringify(body);
    }

    const fullUrl = `${apiConfig.apiBaseUrl}${endpoint}`;
    console.log("Full URL:", fullUrl);

    const res = await fetch(fullUrl, {
      ...options,
      headers,
      body,
    });

    // console.log("Response status:", res.status);
    // console.log("Response headers:", res.headers);

    if (!res.ok) {
      const errorText = await res.text();
      console.error("API Error Response:", errorText);
      throw new Error(`API Error ${res.status}: ${errorText}`);
    }

    // Handle responses that might not be JSON (like DELETE operations)
    const contentType = res.headers.get("content-type");
    let data;

    if (contentType && contentType.includes("application/json")) {
      data = await res.json();
    } else {
      // For non-JSON responses (like plain text), just get the text
      data = await res.text();
    }

    // console.log("API Response data:", data);
    return data;
  } catch (error) {
    console.error("Error in makeApiRequest:", error);
    throw error;
  }
}

// Convenience methods for common HTTP verbs
export const apiClient = {
  get: (endpoint) => makeApiRequest(endpoint),
  post: (endpoint, data) =>
    makeApiRequest(endpoint, {
      method: "POST",
      body: data, // Don't stringify here!
    }),
  put: (endpoint, data) =>
    makeApiRequest(endpoint, {
      method: "PUT",
      body: data, // Don't stringify here!
    }),
  delete: (endpoint) =>
    makeApiRequest(endpoint, {
      method: "DELETE",
    }),
};
